
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { formatUpcomingBookingDate } from '@/utils/dateHelpers';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { useClientUpcomingBookings } from '@/hooks/useClientUpcomingBookings';
import { useAuth } from '@/contexts/AuthContext';
import { Client } from '@/models/Client';
import ClientUpcomingBookingsDialog from './ClientUpcomingBookingsDialog';

interface ClientUpcomingBookingsSectionProps {
  client: Client;
}

const ClientUpcomingBookingsSection = ({ client }: ClientUpcomingBookingsSectionProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currentOrganization } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data: upcomingBookings = [], isLoading } = useClientUpcomingBookings(client.id, 3);

  const handleBookingClick = (booking: any) => {
    // Set calendar filters in localStorage before navigation (organization-specific)
    if (currentOrganization?.id) {
      const filters = {
        selectedDate: booking.booking_date,
        selectedWorkoutTypes: [booking.workout_type_id], // Focus on this specific workout type
      };
      
      const storageKey = `fitness_app_calendar_filters_${currentOrganization.id}`;
      localStorage.setItem(storageKey, JSON.stringify(filters));
    }
    
    // Navigate to calendar with additional params for scrolling
    navigate(`/calendar?date=${booking.booking_date}&timeSlot=${encodeURIComponent(booking.time_slot)}&workoutType=${booking.workout_type_id}`);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="text-center text-muted-foreground">
            {t('common.loading')}...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          {t('clients.upcomingBookings')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {upcomingBookings.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            {t('clients.noUpcomingBookings')}
          </div>
        ) : (
          upcomingBookings.map((booking) => (
            <div
              key={booking.id}
              className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors cursor-pointer"
              onClick={() => handleBookingClick(booking)}
            >
              <div className="flex items-center gap-3">
                <div className="flex flex-col">
                  <span className="font-medium">
                    {formatUpcomingBookingDate(booking.booking_date, i18n.language)}
                  </span>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {booking.time_slot}
                  </div>
                </div>
                <Badge
                  variant="secondary"
                  style={{
                    backgroundColor: booking.workout_type?.color || '#64748b',
                    color: 'white'
                  }}
                >
                  {booking.workout_type?.name}
                </Badge>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </div>
          ))
        )}
        {upcomingBookings.length > 0 && (
          <Button
            variant="outline"
            className="w-full mt-3"
            onClick={() => setDialogOpen(true)}
          >
            {t('clients.viewAllBookings')}
          </Button>
        )}
      </CardContent>

      <ClientUpcomingBookingsDialog 
        client={client}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </Card>
  );
};

export default ClientUpcomingBookingsSection;
